// AppShellHost — the object-nav routing hub (C1 decomposition, task #8).
//
// The central routing hub extracted verbatim from AppShellHost.tsx: the thin
// object-nav handlers (each a dispatch of a named FrameTransition), the ONE
// widened trigger dispatcher (`dispatchTrigger` — determinism M2), and the two
// stamped doors built on it (`applyTrigger` + its ingest twin `applyIngest`).
//
// Frame state itself (the `useReducer(foldFrame, …)`) stays owned by
// AppShellHost — it is read at first paint and inside earlier effects, so it
// cannot move down into this factory without reordering hooks. The hub
// RECEIVES the frame `dispatch` + the current `paneTree` as deps and RETURNS a
// stable-shaped API the rest of the shell consumes (chords, chat, JSX, omnibar).
//
// Breaking the old bidirectional coupling: the routes referenced handlers
// declared FURTHER DOWN in the component (the command palette, settings nav,
// the create dialog, the ingest bridge). Those now arrive as injected
// callbacks (`openCommandPalette`, `gotoSettings`, `openCreateDialog`,
// `applyIngestToHost`) — the shell wraps each
// forward-declared handler in a deferred arrow at the call site, so this hook
// never closes over an as-yet-unassigned local. The arrows still fire only on a
// later event, so the temporal semantics are identical to the inline version.
//
// This hook calls NO React hooks — it is a plain factory of render-local
// handlers, so its call site adds nothing to the component's hook order.

import { mkTimestamp, Result } from "@bilda/utils";
import type { Dispatch } from "react";
import type { ChatEntry } from "../../components/Chat/types.js";
import type { FinderTarget } from "../../components/Finder/finderIndex.js";
import { elementFocusTarget } from "../../components/Navigation/focusTarget.js";
import {
    focusedLeaf,
    leafCycleTab,
    leafLastTab,
    leafTabAtIndex,
    type PaneTree,
} from "../../components/Navigation/paneTree.js";
import type {
    ActiveTab,
    ConversationId,
    FocusTarget,
    ResourceId,
    ResourceRef,
} from "../../components/Navigation/types.js";
import type { ChatCommand } from "../../modules/chat.js";
import type { Signal } from "../../modules/signal.js";
import type { FrameTransition, OverlaySurface } from "../frame.js";
import type { IngestCommand } from "../ingestDecider.js";
import {
    createIntentDispatcher,
    type IntentDispatcher,
} from "../intentDispatch.js";
import type { AppPorts } from "../ports.js";
import type { TriggerProvenance, TriggerSignal } from "../trigger.js";
import { openResourceTransition } from "./resourceHome.js";

export type ObjectNavDeps = {
    // The frame reducer's writer + the current pane tree it produced. Owned by
    // AppShellHost (the `useReducer` stays there); passed in so the hub reads
    // the CURRENT tree at fold time exactly as the inline code did.
    readonly dispatch: Dispatch<FrameTransition>;
    readonly paneTree: PaneTree;
    readonly selectedConversationId: string | null;
    readonly mkConversationId: () => ConversationId;
    readonly ports: AppPorts;
    readonly refFor: (id: ResourceId) => ResourceRef | undefined;
    readonly openOverlay: (overlay: OverlaySurface) => void;
    readonly openFilePicker: () => void;
    // Into the chat module's runtime (mounted in the foundation, S6): a chat
    // command, or a Signal a door raises for the modules.
    readonly sendChat: (command: ChatCommand) => void;
    readonly raiseSignal: (signal: Signal) => void;
    // Forward-declared callbacks injected as deferred arrows (see header): each
    // fires only on a later event, so referencing them here is temporally safe.
    readonly openCommandPalette: (query: string) => void;
    readonly gotoSettings: () => void;
    readonly openCreateDialog: () => void;
    readonly applyIngestToHost: (command: IngestCommand) => void;
};

export type ObjectNav = {
    readonly openConversation: (conversationId: string) => void;
    readonly enterFullChat: () => void;
    readonly openRef: (ref: ResourceRef, target?: FocusTarget) => void;
    readonly onActivateTab: (tab: ActiveTab) => void;
    readonly onFinderJump: (target: FinderTarget) => void;
    readonly goToChange: (
        entryId: string,
        entries: readonly ChatEntry[],
    ) => void;
    readonly onCloseTab: (id: ResourceId) => void;
    readonly reopenClosedTab: () => void;
    readonly dispatchTrigger: IntentDispatcher;
    readonly applyTrigger: (signal: TriggerSignal) => void;
    readonly applyIngest: (
        provenance: TriggerProvenance,
        command: IngestCommand,
    ) => void;
};

// The object-nav handlers (the frame fold advances the state).
// Each handler is a thin dispatch of a named FrameTransition; the fold
// reads the CURRENT frame at fold time, so async completions — the
// ingest batch opening the source it landed — cannot clobber tabs the
// author opened while the round-trip ran.
export const useObjectNav = ({
    dispatch,
    paneTree,
    selectedConversationId,
    mkConversationId,
    ports,
    refFor,
    openOverlay,
    openFilePicker,
    openCommandPalette,
    gotoSettings,
    openCreateDialog,
    sendChat,
    raiseSignal,
    applyIngestToHost,
}: ObjectNavDeps): ObjectNav => {
    // Enter the full presentation: the conversation takes the centre as its
    // OWN TAB in the strip (master audit R6 — the strip never lies about
    // what the centre shows). Reached from the compact box, the panel head
    // and the palette.
    const openConversation = (conversationId: string) =>
        dispatch({ type: "enter-full-chat", conversationId });

    const enterFullChat = () => {
        if (selectedConversationId !== null) {
            openConversation(selectedConversationId);
            return;
        }
        // Open a fresh NASCENT conversation (client-minted id, no durable write —
        // hero-is-initial-conversation-state §3): it renders as the hero composer
        // until the first message starts it durably. No eager `startConversation`.
        const conversationId = mkConversationId();
        sendChat({ type: "conversation_selected", conversationId });
        openConversation(conversationId);
    };

    // Open a resource where it lives (`openResourceTransition`, the ONE
    // routing rule): a source or material in the library detail, a
    // package/file in its tab. Every open funnels through here (library,
    // Home, omnibar, finder, create/duplicate/ingest landings, review links),
    // so the rule lives once. `target` names WHERE in it (a source page, a
    // material element).
    const openRef = (ref: ResourceRef, target?: FocusTarget) =>
        dispatch(openResourceTransition(ref, target));

    const onActivateTab = (tab: ActiveTab) =>
        dispatch({ type: "activate-tab", tab });

    // A content-finder hit was chosen: open the resource it addresses (the
    // Finder closes itself). The element-level scroll to `target.elementPath` is
    // a deferred slice (fuzzy-finder.md MVP cut) — opening the resource tab IS
    // the jump today; scroll-to-element lands later.
    const onFinderJump = (target: FinderTarget) => {
        const ref = refFor(target.resourceId);
        if (ref !== undefined) openRef(ref);
    };

    // "Go to change" from a chat ProposalCard (REQ-UI-002): open the
    // proposal's TARGET material at the changed element, through `openRef`
    // (beside the chat in the right panel; from anywhere else, the library's
    // in-place editor scrolled to the element). Resolving the material +
    // element is a port read (proposalId -> its location).
    const goToChange = (entryId: string, entries: readonly ChatEntry[]) => {
        // Match the PROPOSAL entry specifically: a proposal_marker shares its
        // messageId with the agent message that raised it, so two entries carry
        // the same id — a bare id match can return the agent entry (no
        // proposalId) and miss the proposal.
        const entry = entries.find(
            (e) => e.type === "proposal" && e.id === entryId,
        );
        const proposalId =
            entry?.type === "proposal" ? entry.proposalId : undefined;
        if (proposalId === undefined) return;
        const run = async () => {
            const r = await ports.locateProposal(proposalId);
            if (Result.isErr(r) || r.value === null) return;
            const { materialId, elementId } = r.value;
            const ref = refFor(materialId);
            if (ref === undefined) return;
            openRef(
                ref,
                elementId === undefined
                    ? undefined
                    : elementFocusTarget(elementId),
            );
        };
        void run();
    };

    // Close via the guarded fold: a clean tab closes; a dirty one blocks and
    // the ConfirmDiscard dialog carries the decision.
    const onCloseTab = (id: ResourceId) =>
        dispatch({
            type: "close-tab",
            id,
            freshConversationId: mkConversationId(),
        });

    // Reopen the most recently closed tab (the desktop Cmd+Shift+T): pop the
    // reopen stack and open the resource fresh — an empty stack no-ops.
    const reopenClosedTab = () => dispatch({ type: "reopen-closed-tab" });

    // The widened trigger dispatcher (determinism M2): the ONE choke point for
    // every routed UI signal. `key` keeps the pre-M2 chrome-chord subset;
    // `chat` routes into the chat module's runtime; `frame` to the frame
    // reducer. Deferred arrows close over handlers defined further down
    // without a temporal-dead-zone read at object-construction.
    const dispatchTrigger = createIntentDispatcher({
        ui: (intent) => {
            switch (intent.type) {
                case "open-palette":
                    openCommandPalette("");
                    return;
                case "open-finder":
                    openOverlay({ type: "finder" });
                    return;
                case "open-settings":
                    gotoSettings();
                    return;
                case "toggle-rail":
                    dispatch({ type: "toggle-rail" });
                    return;
                case "summon-assistant":
                    // ⌘J: the Assistant's ONE home is its conversation tab —
                    // open/focus it (the retired corner float is gone). When
                    // that tab is already the centre, entering it again is a
                    // no-op fold that leaves the composer focused.
                    enterFullChat();
                    return;
                case "open-help":
                    openOverlay({ type: "help" });
                    return;
                // Tab + focus-history intents (determinism M5). The keyboard
                // and desktop-menu listeners now speak these UiIntents; the
                // pure `leaf*` folds read the CURRENT paneTree, so the tab
                // target is computed HERE, at the one routed door, exactly as
                // the old inline listeners did.
                case "next-tab":
                    onActivateTab(leafCycleTab(focusedLeaf(paneTree), 1));
                    return;
                case "previous-tab":
                    onActivateTab(leafCycleTab(focusedLeaf(paneTree), -1));
                    return;
                case "select-tab": {
                    const target = leafTabAtIndex(
                        focusedLeaf(paneTree),
                        intent.index,
                    );
                    if (target !== undefined) onActivateTab(target);
                    return;
                }
                case "last-tab":
                    onActivateTab(leafLastTab(focusedLeaf(paneTree)));
                    return;
                case "close-active-tab":
                    dispatch({
                        type: "close-active-tab",
                        freshConversationId: mkConversationId(),
                    });
                    return;
                case "reopen-closed-tab":
                    reopenClosedTab();
                    return;
                case "nav-back":
                    dispatch({ type: "nav-back" });
                    return;
                case "nav-forward":
                    dispatch({ type: "nav-forward" });
                    return;
                // Authoring intents (determinism M5) — the desktop File-menu
                // "New material" / "Add source". Each opens its shell surface
                // (the create dialog resets its own local state; the picker
                // raises the OS file dialog via the IngestHost ref).
                case "new-material":
                    openCreateDialog();
                    return;
                case "add-source":
                    openFilePicker();
                    return;
                // Escape while drafting: a stop request for the selected
                // conversation, which chat owns (its inbound).
                case "stop-turn":
                    if (selectedConversationId !== null)
                        raiseSignal({
                            _tag: "Signal.StopRequested",
                            conversationId: selectedConversationId,
                        });
                    return;
                default:
                    // The remaining UiIntents (open-finder is handled above;
                    // dismiss-overlay is not keyboard/menu-fired here) have no
                    // route on this door yet.
                    throw new Error(
                        `UiIntent ${intent.type} is not routed by the shell dispatcher`,
                    );
            }
        },
        domain: (command) => {
            // No DomainCommand enters through the chrome chord subset yet.
            // The dispatcher already captures its resolved DTO; concrete
            // port routing lands when clicks widen onto this seam.
            throw new Error(
                `domain command ${command} is not routed by the chrome keyboard subset`,
            );
        },
        chat: sendChat,
        frame: (transition) => dispatch(transition),
        // Ingest's decide→evolve→interpret bridge lives in IngestHost (it
        // owns the batch state), reached via the same imperative handle the
        // shell already uses for `open()` — the SAME channel, not a new one.
        // A routed ingest trigger only ever fires while IngestHost is
        // mounted (it originated the trigger), so the ref is always set.
        ingest: (command) => applyIngestToHost(command),
    });

    // The ONE stamped door (determinism M2). A call site hands the two facts it
    // alone knows — the signal's provenance and its action; this reads the clock
    // ONCE at the shell edge, stamps `at`, and routes the fully-formed
    // `UiTrigger`. The clock read lives here (impure shell), never in the pure
    // deciders downstream.
    const applyTrigger = (signal: TriggerSignal) =>
        dispatchTrigger({
            ...signal,
            at: mkTimestamp(new Date().toISOString()),
        });

    // Feed an ingest command through the ONE stamped trigger door (determinism
    // M9.2). Every ingest signal — a user
    // drag/drop/pick, a per-file port resolution re-entering, the batch coda —
    // enters HERE with its provenance, so the whole batch is one trigger
    // sequence through the single door. The command is routed (via the
    // dispatcher's `ingest` route) back into IngestHost's decider bridge. Passed
    // down to IngestHost as a prop, mirroring how `openRef`/`showNotice` are.
    const applyIngest = (
        provenance: TriggerProvenance,
        command: IngestCommand,
    ) => applyTrigger({ provenance, action: { type: "ingest", command } });

    return {
        openConversation,
        enterFullChat,
        openRef,
        onActivateTab,
        onFinderJump,
        goToChange,
        onCloseTab,
        reopenClosedTab,
        dispatchTrigger,
        applyTrigger,
        applyIngest,
    };
};
